

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
    items: any;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    // ingredients: ingredients;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const foodintolernece: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "food-intolerance-test",
        "meta": {
            "title": "Food Intolerance Test | Elite Body Home",
            "description": "Discover which foods your body struggles to digest and improve your health with our comprehensive food intolerance testing.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/food-intolerance-test/",
            "og": {
                "title": "Food Intolerance Test | Elite Body Home",
                "description": "Identify problem foods that may be causing digestive issues and other health problems",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-53.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Food Intolerance Test"],
        "hero": {
            "title": "Food Intolerance Test",
            "subtitle": "Discover the foods your body doesn't tolerate for better health",
            "description": "Food intolerance is a common problem that affects many people worldwide, potentially leading to annoying digestive disorders and unwanted health symptoms. This test helps you identify foods that your body has difficulty digesting, allowing you to make effective dietary changes to improve your overall health."
        },
        "howItWorks": {
            "title": "What's Included in Our Food Intolerance Test",
            "description": "Our comprehensive test panel helps identify various food intolerances through advanced testing methods.",
            "items": [
                "IgG Antibody Test: Measures IgG antibodies against specific foods",
                "Comprehensive Food Panel: Includes testing for intolerance to a wide range of foods",
                "Histamine Level Assessment: Evaluates body's response to histamine-rich foods",
                "Lactose Intolerance Test: Assesses ability to digest lactose in dairy products",
                "Gluten Sensitivity Test: Helps detect sensitivity to gluten",
                "Celiac Disease Screening (if needed): Immunological test for autoimmune response to gluten",
                "Fructose Intolerance Test: Evaluates ability to absorb and digest fructose",
                "Food Additives Sensitivity Test: Detects reactions to artificial preservatives, colors and flavors"
            ]
        },
        "procedureSteps": {
            "heading": "Procedure",
            "para": "The food intolerance test includes several methods to detect the body's sensitivity to certain types of foods, the most common being blood tests, skin tests, or elimination diets. These methods help identify foods that may cause symptoms such as bloating, diarrhea, headaches, or fatigue.",
            "steps": [
                {
                    "title": "Initial Consultation",
                    "desc": "Discussion of symptoms, eating habits and health concerns with a specialist doctor.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Blood Sample Collection",
                    "desc": "A simple blood test is required to measure IgG antibody levels against specific foods.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Laboratory Analysis",
                    "desc": "The sample is examined to check the body's reaction to different groups of foods and food additives.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "Doctor's Review",
                    "desc": "A consultation session with the doctor to review the results and develop a customized nutritional plan based on the tests.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "Health Report",
                    "desc": "You receive a comprehensive report showing the types of foods that are likely to cause sensitivity or intolerance.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Ongoing Support",
                    "desc": "You'll receive ongoing nutritional guidance to help modify your diet and manage intolerance cases effectively.",
                    "icon": "fa-solid fa-hands-helping"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "A food intolerance test can make a big difference in improving your quality of life by identifying foods that cause you discomfort or health problems. Here are the main benefits this test offers:",
            "items": [
                {
                    "title": "Customized Nutritional Guidance",
                    "desc": "The results help design a diet that avoids foods that cause you allergies, reducing digestive disorders.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "Improved Digestion",
                    "desc": "You can enjoy better digestion and reduce annoying symptoms like bloating, gas and indigestion by avoiding foods that upset the digestive system.",
                    "icon": "fa-solid fa-stomach"
                },
                {
                    "title": "Increased Energy Levels",
                    "desc": "Food intolerance cases may cause fatigue, and modifying the diet helps boost activity and vitality throughout the day.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Relief from Chronic Symptoms",
                    "desc": "Symptoms of intolerance to some foods sometimes appear as headaches, skin problems, or joint pain, and identifying and excluding these foods from the diet helps get rid of these persistent symptoms.",
                    "icon": "fa-solid fa-allergies"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "A food intolerance test is recommended for people who suffer from persistent digestive problems or unexplained symptoms after eating. This test is particularly useful for the following groups:",
            "items": [
                {
                    "title": "People with persistent bloating, gas or diarrhea after eating",
                    "desc": "",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "Individuals who suffer from headaches, migraines, or fatigue related to diet",
                    "desc": "",
                    "icon": "fa-solid fa-head-side-virus"
                },
                {
                    "title": "People with skin problems such as eczema or rashes that may be caused by eating certain foods",
                    "desc": "",
                    "icon": "fa-solid fa-allergies"
                },
                {
                    "title": "Anyone who suspects that certain foods affect their health or general comfort",
                    "desc": "",
                    "icon": "fa-solid fa-question"
                },
                {
                    "title": "People who have difficulty losing weight or suffer from unexplained weight gain",
                    "desc": "",
                    "icon": "fa-solid fa-weight"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "Preparing for a food intolerance test is easy and simple, and requires following just a few steps to ensure accurate results. Here are the guidelines you should follow before taking the test:",
            "items": [
                {
                    "title": "Continue your usual diet",
                    "desc": "Make sure to eat the foods you usually eat in the days leading up to the test, so that the results reflect your body's actual response to food.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Avoid certain medications",
                    "desc": "Some medications may affect test results. Consult your doctor to find out if you should stop any medications before the test.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Stay hydrated",
                    "desc": "Drink enough water before the test, as it helps facilitate blood sample collection and ensures the body is in a normal state during the test.",
                    "icon": "fa-solid fa-tint"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "The results of the food intolerance test are presented in a detailed report showing the foods that provoke an immune response or cause digestive problems for the person. Based on the results, you can:",
            "items": [
                {
                    "title": "Stop or reduce eating certain foods",
                    "desc": "",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Work with a nutritionist to design a balanced diet free of unsuitable foods",
                    "desc": "",
                    "icon": "fa-solid fa-handshake"
                },
                {
                    "title": "Monitor your symptoms to notice how changes in your diet affect your health",
                    "desc": "",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Why Choose Elite Body Home Clinics?",
            "subheading": "",
            "para": "With over 25 years of experience, our qualified medical team delivers results focused on client needs.",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How accurate are food intolerance tests?",
                    "answer": "Our IgG antibody tests have high accuracy rates when properly conducted and interpreted by our specialists in conjunction with your symptoms."
                },
                {
                    "question": "What's the difference between food allergy and intolerance?",
                    "answer": "Food allergies involve the immune system and can be life-threatening, while intolerances typically cause digestive discomfort and other non-life-threatening symptoms."
                },
                {
                    "question": "How long do I need to eliminate problem foods?",
                    "answer": "This varies by individual, but many people see improvement within 2-6 weeks of eliminating problematic foods identified in testing."
                },
                {
                    "question": "Can children take food intolerance tests?",
                    "answer": "Yes, we offer testing for children aged 3 and above who may be experiencing food-related symptoms."
                }
            ]
        }
    },
    "ar": {
        "url": "اختبار-عدم-تحمل-الطعام",
        "meta": {
            "title": "اختبار عدم تحمل الطعام | ايليت بودي هوم",
            "description": "اكتشف أنواع الطعام التي لا يتقبلها جسمك لتحظى بصحة أفضل وجودة حياة أعلى",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/ar/services/اختبار-عدم-تحمل-الطعام",
            "og": {
                "title": "اختبار عدم تحمل الطعام | ايليت بودي هوم",
                "description": "فحص متكامل للكشف عن الأطعمة التي تسبب اضطرابات هضمية وأعراض صحية غير مرغوب فيها",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-53.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "اختبار عدم تحمل الطعام"],
        "hero": {
            "title": "اختبار عدم تحمل الطعام",
            "subtitle": "اكتشف أنواع الطعام التي لا يتقبلها جسمك لتحظى بصحة أفضل",
            "description": "يُعد عدم تحمل الطعام من المشكلات الشائعة التي تؤثر على العديد من الأشخاص حول العالم، وقد تؤدي إلى اضطرابات هضمية مزعجة وأعراض صحية غير مرغوب فيها. يساعدك هذا الفحص في تحديد الأطعمة التي يواجه جسمك صعوبة في هضمها، مما يتيح لك إجراء تغييرات غذائية فعالة لتحسين صحتك العامة."
        },
        "howItWorks": {
            "title": "ماذا يتضمن اختبار عدم تحمل الطعام لدينا؟",
            "description": "نقدم في عيادات ايليت بودي هوم اختبار عدم تحمل الطعام المتكامل الذي يقيس استجابة الجسم لمجموعة واسعة من الأطعمة والإضافات الغذائية.",
            "items": [
                "اختبار الأجسام المضادة IgG: يقيس وجود أجسام مضادة من نوع IgG ضد أنواع محددة من الأطعمة",
                "لوحة طعام شاملة: يتضمن فحص متكامل للكشف عن عدم تحمل مجموعة واسعة من الأطعمة",
                "مستويات تفاعل الجسم مع الهيستامين: يتم تقييم استجابة الجسم للأطعمة التي تحتوي على الهيستامين",
                "اختبار عدم تحمل اللاكتوز: يُقيم قدرة الجسم على هضم اللاكتوز الموجود في منتجات الألبان",
                "اختبار حساسية الغلوتين: يساعد في الكشف عن مدى حساسية الجسم تجاه الغلوتين",
                "الفحص عن مرض سيلياك (إذا لزم الأمر): تحليل مناعي للكشف عن وجود استجابة مناعية ذاتية تجاه الغلوتين",
                "اختبار عدم تحمل الفركتوز: يُقيم قدرة الجسم على امتصاص وهضم الفركتوز",
                "اختبار الحساسية من الإضافات الغذائية: يساهم في الكشف عن ردود فعل الجسم تجاه المواد الحافظة الصناعية"
            ]
        },
        "procedureSteps": {
            "heading": "الإجراء",
            "para": "يشمل اختبار عدم تحمل الطعام عدة طرق للكشف عن حساسية الجسم تجاه أنواع معينة من الأطعمة، وأكثرها شيوعًا فحوصات الدم، واختبارات الجلد، أو حمية الإقصاء. تساعد هذه الطرق في تحديد الأطعمة التي قد تسبب أعراضًا مثل الانتفاخ، أو الإسهال، أو الصداع، أو الإرهاق.",
            "steps": [
                {
                    "title": "استشارة أولية",
                    "desc": "يتم مناقشة الأعراض والعادات الغذائية والمخاوف الصحية مع الطبيب المتخصص.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "جمع عينة الدم",
                    "desc": "يتطلب إجراء تحليل دم بسيط لقياس مستويات الأجسام المضادة من نوع IgG تجاه أطعمة معينة.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "التحليل المخبري",
                    "desc": "تُفحص العينة للتحقق من تفاعل الجسم مع مجموعات مختلفة من الأطعمة والإضافات الغذائية.",
                    "icon": "fa-solid fa-microscope"
                },
                {
                    "title": "مراجعة الطبيب",
                    "desc": "تُجرى جلسة استشارية مع الطبيب لمراجعة النتائج ووضع خطة غذائية مخصصة بناءً على التحاليل.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "تقرير صحي",
                    "desc": "يُسلم تقرير شامل يوضح أنواع الأطعمة التي يُحتمل وجود تحسس منها أو عدم تحمل لها.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "دعم ومتابعة مستمرة",
                    "desc": "ستُزود بإرشادات غذائية مستمرة للمساعدة في تعديل النظام الغذائي وإدارة حالات عدم التحمل بشكل فعال.",
                    "icon": "fa-solid fa-hands-helping"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "يمكن لاختبار عدم تحمل الطعام أن يُحدث فرقًا كبيرًا في تحسين جودة حياتك من خلال تحديد الأطعمة التي تُسبب لك الازعاج أو المشكلات الصحية. إليك أبرز الفوائد التي يقدمها هذا الاختبار:",
            "items": [
                {
                    "title": "إرشادات غذائية مخصصة",
                    "desc": "تساعد النتائج في تصميم نظام غذائي يتجنب الأطعمة التي تسبب لك الحساسية، مما يقلل من الاضطرابات الهضمية.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "تحسين عملية الهضم",
                    "desc": "يمكنك الاستمتاع بهضم أفضل وتقليل الأعراض المزعجة مثل الانتفاخ والغازات وعسر الهضم من خلال تجنب الأطعمة التي تزعج الجهاز الهضمي.",
                    "icon": "fa-solid fa-stomach"
                },
                {
                    "title": "زيادة مستويات الطاقة",
                    "desc": "قد يسبب حالات عدم تحمل الطعام الشعور بالإرهاق، ويساعد تعديل النظام الغذائي في تعزيز النشاط والحيوية طوال اليوم.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "التخفيف من الأعراض المزمنة",
                    "desc": "تظهر أعراض عدم تحمل بعض الأطعمة أحيانًا على شكل صداع، أو مشكلات جلدية، أو آلام في المفاصل، ويساعد تحديد هذه الأطعمة واستبعادها من النظام الغذائي في التخلص من هذه الأعراض المستمرة.",
                    "icon": "fa-solid fa-allergies"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يُنصح بإجراء اختبار عدم تحمل الطعام للأشخاص الذين يعانون من مشكلات هضمية مستمرة أو أعراض غير مبررة بعد تناول الطعام. حيث يُعد هذا الاختبار مفيدًا بشكل خاص للفئات التالية:",
            "items": [
                {
                    "title": "الأشخاص الذين يعانون من انتفاخ مستمر، أو غازات أو إسهال بعد تناول الطعام",
                    "desc": "",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "الأفراد الذين يعانون من الصداع، أو الصداع النصفي، أو الإرهاق المرتبط بالنظام الغذائي",
                    "desc": "",
                    "icon": "fa-solid fa-head-side-virus"
                },
                {
                    "title": "الأشخاص الذين يعانون من مشكلات جلدية مثل الإكزيما أو الطفح الجلدي والتي قد تكون ناتجة عن تناول أطعمة محددة",
                    "desc": "",
                    "icon": "fa-solid fa-allergies"
                },
                {
                    "title": "أي شخص يشتبه في أن أطعمة محددة تؤثر على صحته أو شعوره العام بالراحة",
                    "desc": "",
                    "icon": "fa-solid fa-question"
                },
                {
                    "title": "الأشخاص الذين يواجهون صعوبة في فقدان الوزن أو يعانون من زيادة وزن غير مبررة",
                    "desc": "",
                    "icon": "fa-solid fa-weight"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يُعد التحضير لاختبار عدم تحمل الطعام سهل وبسيط، ويتطلب اتباع بعض الخطوات فقط لضمان دقة النتائج. إليك الإرشادات التي يُفضل اتباعها قبل إجراء الاختبار:",
            "items": [
                {
                    "title": "الاستمرار في تناول نظامك الغذائي المعتاد",
                    "desc": "احرص على تناول الأطعمة التي تتناولها عادةً في الأيام التي تسبق الاختبار، حتى تعكس النتائج استجابة جسمك الفعلية للطعام.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "تجنب بعض الأدوية",
                    "desc": "قد تؤثر بعض الأدوية على نتائج الاختبار. استشر الطبيب لمعرفة ما إذا كان ينبغي التوقف عن أي أدوية قبل إجراء التحليل.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "اشرب كمية كافية من الماء قبل الاختبار، حيث يساعد في تسهيل سحب عينة الدم، ويضمن أن يكون الجسم في حالة طبيعية أثناء التحليل.",
                    "icon": "fa-solid fa-tint"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "تُعرض نتائج اختبار عدم تحمل الطعام في تقرير مفصل يوضح الأطعمة التي تُثير الاستجابة مناعية أو تسبب مشكلات في الهضم لدى الشخص. استنادًا إلى النتائج، يمكنك:",
            "items": [
                {
                    "title": "التوقف أو تقليل تناول بعض الأطعمة",
                    "desc": "",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "التعاون مع أخصائي تغذية لتصميم نظام غذائي متوازن خالٍ من الأطعمة غير المناسبة لك",
                    "desc": "",
                    "icon": "fa-solid fa-handshake"
                },
                {
                    "title": "مراقبة أعراضك لتلاحظ كيف تؤثر التغييرات في نظامك الغذائي على صحتك",
                    "desc": "",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "aftercareTips": {
            "heading": "لماذا يجب اختيار عيادات ايليت بودي هوم؟",
            "subheading": "",
            "para": "خبرة تمتد لأكثر من 25 عامًا، فريق طبي مؤهل، نتائج تركز على احتياجات العميل.",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "",
            "items": [
                {
                    "question": "ما مدى دقة اختبارات عدم تحمل الطعام؟",
                    "answer": "تتمتع اختبارات الأجسام المضادة IgG لدينا بمعدلات دقة عالية عند إجرائها وتفسيرها بشكل صحيح من قبل أخصائيينا بالاقتران مع أعراضك."
                },
                {
                    "question": "ما الفرق بين حساسية الطعام وعدم التحمل؟",
                    "answer": "تشمل حساسية الطعام الجهاز المناعي ويمكن أن تهدد الحياة، بينما تسبب حالات عدم التحمل عادةً عدم راحة في الجهاز الهضمي وأعراضًا أخرى غير مهددة للحياة."
                },
                {
                    "question": "كم من الوقت أحتاج لتجنب الأطعمة المسببة للمشاكل؟",
                    "answer": "يختلف هذا باختلاف الفرد، لكن العديد من الأشخاص يلاحظون تحسنًا في غضون 2-6 أسابيع من التوقف عن تناول الأطعمة المسببة للمشاكل المحددة في الاختبار."
                },
                {
                    "question": "هل يمكن للأطفال إجراء اختبارات عدم تحمل الطعام؟",
                    "answer": "نعم، نقدم اختبارات للأطفال من سن 3 سنوات فما فوق الذين قد يعانون من أعراض مرتبطة بالطعام."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/elementor/thumbs/Food-Intolerance-Test-1-scaled-1-r1rxwkcuscx1hg6pmouvjchg2ohvjlo1kkeakl32cs.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 18,
            "title": "Comprehensive Food Intolerance Test",
            "description": "Advanced testing to identify foods your body struggles to digest",
            "price": 1500
        }
    }
}